import React, { useEffect, useState } from "react";
import { Shell, Section } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function AdminRewards() {
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", cost_points: 500, active: true });

  const load = async () => {
    const [r, red] = await Promise.all([api.get("/admin/rewards"), api.get("/admin/redemptions")]);
    setRewards(r.data); setRedemptions(red.data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/rewards", { ...form, cost_points: Number(form.cost_points) });
      toast.success("Reward created");
      setForm({ name: "", description: "", cost_points: 500, active: true });
      load();
    } catch (err) { toast.error("Failed", { description: formatApiError(err) }); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/redemptions/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/admin/rewards/${r.id}`, { name: r.name, description: r.description, cost_points: r.cost_points, active: !r.active });
      load();
    } catch (e) { toast.error("Failed", { description: formatApiError(e) }); }
  };

  return (
    <Shell title="Rewards & Payouts" portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Section title="Reward catalog" testId="section-admin-rewards">
            {rewards.length === 0 ? <div className="text-mute text-sm py-8 text-center" data-testid="rewards-empty">No rewards yet.</div> : (
              <table className="w-full text-sm" data-testid="rewards-table">
                <thead><tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                  <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Description</th><th className="py-3 pr-4 text-right">Cost</th><th className="py-3 pr-4">Active</th><th className="py-3" />
                </tr></thead>
                <tbody>
                  {rewards.map((r) => (
                    <tr key={r.id} className="border-b border-white/5" data-testid={`reward-row-${r.id}`}>
                      <td className="py-3 pr-4 font-medium">{r.name}</td>
                      <td className="py-3 pr-4 text-mute text-xs">{r.description}</td>
                      <td className="py-3 pr-4 text-right font-mono">{r.cost_points}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${r.active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-mute border-white/10"}`}>{r.active ? "active" : "off"}</span>
                      </td>
                      <td className="py-3 text-right">
                        <button onClick={() => toggleActive(r)} data-testid={`reward-toggle-${r.id}`} className="text-xs text-indigo-300 hover:underline">Toggle</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>
        <form onSubmit={create} className="glass rounded-2xl p-6 h-fit" data-testid="new-reward-form">
          <h3 className="font-display font-bold text-lg mb-4">New reward tier</h3>
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Name</label>
          <input required data-testid="new-reward-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="in mb-3" />
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Description</label>
          <input data-testid="new-reward-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="in mb-3" />
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Cost (points)</label>
          <input required type="number" data-testid="new-reward-cost" value={form.cost_points} onChange={(e) => setForm({ ...form, cost_points: e.target.value })} className="in mb-4" />
          <button data-testid="new-reward-submit" className="w-full bg-indigo-500 hover:bg-indigo-400 rounded-md py-2 text-sm font-medium inline-flex items-center justify-center gap-2">
            <Plus className="w-4 h-4" /> Create reward
          </button>
          <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}
.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
        </form>
      </div>

      <div className="mt-8">
        <Section title="Payout queue" testId="section-payouts">
          {redemptions.length === 0 ? <div className="text-mute text-sm py-8 text-center" data-testid="payouts-empty">No redemptions yet.</div> : (
            <table className="w-full text-sm" data-testid="payouts-table">
              <thead><tr className="text-left text-xs uppercase tracking-widest text-mute border-b border-white/10">
                <th className="py-3 pr-4">Panelist</th><th className="py-3 pr-4">Reward</th><th className="py-3 pr-4 text-right">Cost</th><th className="py-3 pr-4">Status</th><th className="py-3 pr-4">Date</th><th className="py-3" />
              </tr></thead>
              <tbody>
                {redemptions.map((r) => (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`payout-row-${r.id}`}>
                    <td className="py-3 pr-4 font-mono text-xs">{r.respondent_email}</td>
                    <td className="py-3 pr-4">{r.reward_name}</td>
                    <td className="py-3 pr-4 text-right font-mono">{r.cost_points}</td>
                    <td className="py-3 pr-4 uppercase text-xs tracking-widest">{r.status}</td>
                    <td className="py-3 pr-4 text-mute text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="py-3 text-right space-x-1">
                      {r.status === "pending" && <button onClick={() => setStatus(r.id, "approved")} data-testid={`payout-approve-${r.id}`} className="text-xs bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-md px-2 py-1">Approve</button>}
                      {r.status !== "paid" && r.status !== "rejected" && <button onClick={() => setStatus(r.id, "paid")} data-testid={`payout-mark-paid-${r.id}`} className="text-xs bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 rounded-md px-2 py-1">Mark paid</button>}
                      {(r.status === "pending" || r.status === "approved") && <button onClick={() => setStatus(r.id, "rejected")} data-testid={`payout-reject-${r.id}`} className="text-xs bg-rose-500/15 border border-rose-500/30 text-rose-300 rounded-md px-2 py-1">Reject</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </div>
    </Shell>
  );
}
